/**
 * Kanban Page
 * Dedicated kanban view with filters and swimlanes
 */

import React, { useState, useRef, useEffect, useCallback  } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Users, Plus, ZoomIn, ZoomOut, LayoutGrid } from 'lucide-react';
import { KanbanBoard } from '@/components/kanban/KanbanBoard';
import { CreateTicketModal } from '@/components/tickets/CreateTicketModal';
import { TicketSearch } from '@/components/tickets/TicketSearch';
import { useTickets } from '@/hooks';
import { LoadingState } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { TicketFilters as Filters } from '@/types';
import { useProjectStore } from '@/store/projectStore';
import Select from 'react-select';
import { userService } from '@/services/userService';

export function KanbanPage() {
  const currentProject = useProjectStore((state) => state.currentProject);
  const [searchParams] = useSearchParams();
  const projectId = currentProject?.id || searchParams.get('project') || 'default-project-id';
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef<any>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    limit: 100,
  });

  // Zoom & compact state (persisted in localStorage)
  const [zoom, setZoom] = useState<number>(() => {
    const saved = localStorage.getItem('kanban-zoom');
    return saved ? parseFloat(saved) : 1;
  });
  const [compact, setCompact] = useState<boolean>(() => {
    return localStorage.getItem('kanban-compact') === 'true';
  });

  const handleZoomChange = useCallback((delta: number) => {
    setZoom((prev) => {
      const next = Math.min(1.5, Math.max(0.5, parseFloat((prev + delta).toFixed(2))));
      localStorage.setItem('kanban-zoom', String(next));
      return next;
    });
  }, []);

  const handleCompactToggle = useCallback(() => {
    setCompact((prev) => {
      const next = !prev;
      localStorage.setItem('kanban-compact', String(next));
      return next;
    });
  }, []);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    userService.getUsers().then((res) => {
      setUsers(res || []);
    });
  }, []);

  const assigneeOptions = users.map((u: any) => ({
    value: u.id,
    label: u.first_name + ' ' + u.last_name,
  }));
  


  const priorityOptions = [
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ];

  const typeOptions = [
    { value: 'bug', label: 'Bug' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'support_ticket', label: 'Support Ticket' },
    { value: 'task', label: 'Task' },
    { value: 'epic', label: 'Epic' },
  ];
  


  const { data, isLoading, error, refetch } = useTickets(projectId, filters);

  const handleSearchChange = (search: string) => {
  setSearchQuery(search);

  if (debounceRef.current) {
    clearTimeout(debounceRef.current);
  }

  debounceRef.current = setTimeout(() => {
    setFilters((prev: any) => ({
      ...prev,
      search: search || undefined,
    }));
  }, 400); // little higher = smoother
};

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LoadingState message="Loading kanban board..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to load tickets'}
          retry={() => refetch()}
        />
      </div>
    );
  }

  const tickets = data?.data || [];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="max-w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">Kanban Board</h1>
                {currentProject && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                    {currentProject.name}
                  </span>
                )}
              </div>

              {/* Search */}
              <div className="max-w-md flex-1">
                <TicketSearch
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search tickets..."
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </button>

              <button
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Users className="h-4 w-4 mr-2" />
                Swimlanes
              </button>

              {/* Compact toggle */}
              <button
                onClick={handleCompactToggle}
                title={compact ? 'Switch to normal view' : 'Switch to compact view'}
                className={`inline-flex items-center px-3 py-2 border rounded-md shadow-sm text-sm font-medium transition-colors ${
                  compact
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>

              {/* Zoom controls */}
              <div className="inline-flex items-center border border-gray-300 rounded-md shadow-sm overflow-hidden">
                <button
                  onClick={() => handleZoomChange(-0.1)}
                  disabled={zoom <= 0.5}
                  className="px-2 py-2 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-40 border-r border-gray-300"
                  title="Zoom out"
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <span className="px-2 text-xs text-gray-600 font-medium bg-white">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={() => handleZoomChange(0.1)}
                  disabled={zoom >= 1.5}
                  className="px-2 py-2 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-40 border-l border-gray-300"
                  title="Zoom in"
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
              </div>

              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Ticket
              </button>
            </div>
          </div>


          {/* filters section */}
          {showFilters && (
            <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap gap-4 items-end mt-8">

              {/* Priority */}
              <div className="min-w-[240px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Priority
                </label>

                <Select
                  isMulti
                  options={priorityOptions}
                  placeholder="Select priority..."
                  className="text-sm"
                  value={priorityOptions.filter((o) =>
                    filters.priority?.includes(o.value)
                  )}
                  onChange={(selected) => {
                    setFilters((prev: any) => ({
                      ...prev,
                      priority:
                        selected.length > 0
                          ? selected.map((s) => s.value)
                          : undefined,
                    }));
                  }}
                />
              </div>

              {/* Type */}
              <div className="min-w-[260px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Type
                </label>

                <Select
                  isMulti
                  options={typeOptions}
                  placeholder="Select type..."
                  className="text-sm"
                  value={typeOptions.filter((o) =>
                    filters.type?.includes(o.value)
                  )}
                  onChange={(selected) => {
                    setFilters((prev: any) => ({
                      ...prev,
                      type:
                        selected.length > 0
                          ? selected.map((s) => s.value)
                          : undefined,
                    }));
                  }}
                />
              </div>


              {/* Assignee */}
              <div className="min-w-[260px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Assignee
                </label>

                <Select
                  isMulti
                  options={assigneeOptions}
                  placeholder="Select assignee..."
                  className="text-sm"
                  value={assigneeOptions.filter((o) =>
                    filters.assignee_id?.includes(o.value)
                  )}
                  onChange={(selected: any) => {
                    setFilters((prev: any) => ({
                      ...prev,
                      assignee_id:
                        selected.length > 0
                          ? selected.map((s: any) => s.value)
                          : undefined,
                    }));
                  }}
                />
              </div>

              <button
                onClick={() => {
                  setFilters({ limit: 100, assignee_id: undefined, });
                  setSearchQuery('');
                }}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Ticket Count */}
          <div className="mt-2 text-sm text-gray-600">
            {tickets.length} tickets
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-hidden px-4 sm:px-6 lg:px-8 py-6">
        <div
          className="h-full origin-top-left transition-transform duration-150"
          style={{ transform: `scale(${zoom})`, width: `${100 / zoom}%`, height: `${100 / zoom}%` }}
        >
          <KanbanBoard
            tickets={tickets}
            onAddTicket={() => setIsCreateModalOpen(true)}
            compact={compact}
          />
        </div>
      </div>

      {/* Create Modal */}
      <CreateTicketModal
        projectId={projectId}
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  );
}
